import {DiskSize, HostType, RawGreenplumConfig} from 'components/calculator/GreenplumForm/types';
import {cloneDeep} from 'lodash';
import {
    DISK_TYPE_ID_ORDER,
    GENERATION_ORDER,
    PRESET_TYPE_ORDER,
} from 'components/calculator/GreenplumForm/constants';

export type GreenplumPreparedConfig = ReturnType<typeof prepareConfig>;
export type NormGreenplumConfig = ReturnType<typeof normalizeConfig>;
export type NormHost = NormGreenplumConfig['hostTypes'][number];
export type NormResourcePreset = NormHost['resourcePresets'][number];
export type NormPresetZoneDisk = NormResourcePreset['zones'][number]['diskTypes'][number];

export const normalizeConfig = (origRawConfig: RawGreenplumConfig) => {
    const rawConfig = cloneDeep(origRawConfig);
    const zoneIdHostTypeSetMap = new Map<string, Set<string>>();
    const generationSet = new Set<string>();
    const presetTypeSet = new Set<string>();
    const diskTypeIdSet = new Set<string>();

    const config = {
        ...rawConfig,
        hostTypes: rawConfig.hostTypes.map((hostType) => {
            return {
                ...hostType,
                resourcePresets: hostType.resourcePresets.map((resourcePreset) => {
                    generationSet.add(resourcePreset.generation);
                    presetTypeSet.add(resourcePreset.type);

                    return {
                        ...resourcePreset,
                        zones: resourcePreset.zones.map((zone) => {
                            let hostTypeSet = zoneIdHostTypeSetMap.get(zone.zoneId);
                            if (!hostTypeSet) {
                                zoneIdHostTypeSetMap.set(
                                    zone.zoneId,
                                    (hostTypeSet = new Set<string>()),
                                );
                            }

                            hostTypeSet.add(hostType.type);

                            return {
                                ...zone,
                                diskTypes: zone.diskTypes.map((diskType) => {
                                    diskTypeIdSet.add(diskType.diskTypeId);

                                    const props = {
                                        minHosts: Number(diskType.minHosts),
                                        maxHosts: Number(diskType.maxHosts),
                                        hostCountDivider: Number(diskType.hostCountDivider),
                                        maxSegmentInHostCount: Number(
                                            diskType.maxSegmentInHostCount,
                                        ),
                                    };
                                    if (diskType.DiskSize === DiskSize.DISK_SIZES) {
                                        return {
                                            ...diskType,
                                            ...props,
                                            diskSizes: {
                                                sizes: diskType.diskSizes.sizes.map((size) =>
                                                    Number(size),
                                                ),
                                            },
                                        };
                                    } else {
                                        return {
                                            ...diskType,
                                            ...props,
                                            diskSizeRange: {
                                                min: Number(diskType.diskSizeRange.min),
                                                max: Number(diskType.diskSizeRange.max),
                                            },
                                        };
                                    }
                                }),
                            };
                        }),
                        cpuLimit: Number(resourcePreset.cpuLimit),
                        memoryLimit: Number(resourcePreset.memoryLimit),
                        cpuFraction: Number(resourcePreset.cpuFraction),
                    };
                }),
                defaultResources: {
                    ...hostType.defaultResources,
                    diskSize: Number(hostType.defaultResources.diskSize),
                },
            };
        }),
    };

    const availableZoneIds: string[] = [];
    zoneIdHostTypeSetMap.forEach((hostTypeSet, zoneId) => {
        if (hostTypeSet.size === config.hostTypes.length) {
            availableZoneIds.push(zoneId);
        }
    });

    if (availableZoneIds.length && !availableZoneIds.includes(config.defaultZoneId)) {
        config.defaultZoneId = availableZoneIds[0];
    }

    generationSet.forEach((gen) => {
        if (!GENERATION_ORDER.includes(gen)) {
            GENERATION_ORDER.push(gen);
        }
    });

    presetTypeSet.forEach((type) => {
        if (!PRESET_TYPE_ORDER.includes(type)) {
            PRESET_TYPE_ORDER.push(type);
        }
    });

    diskTypeIdSet.forEach((typeId) => {
        if (!DISK_TYPE_ID_ORDER.includes(typeId)) {
            DISK_TYPE_ID_ORDER.push(typeId);
        }
    });

    return config;
};

export const prepareConfig = (origConfig: NormGreenplumConfig, currentHostType: HostType) => {
    const config = cloneDeep(origConfig);
    const {defaultZoneId} = config;

    const typeHostMap = new Map<HostType, NormHost>();

    config.hostTypes = config.hostTypes.filter((host) => {
        host.resourcePresets = host.resourcePresets.filter((resourcePreset) => {
            if (resourcePreset.decomissioning) {
                return false;
            }

            resourcePreset.zones = resourcePreset.zones.filter((zone) => {
                if (zone.zoneId !== defaultZoneId) {
                    return false;
                }

                return true;
            });

            return resourcePreset.zones.length;
        });

        if (host.resourcePresets.length) {
            typeHostMap.set(host.type, host);
            return true;
        }
        return false;
    });

    const host = typeHostMap.get(currentHostType);
    if (!host) {
        throw new Error('Host type is not found');
    }

    const {defaultResources, resourcePresets} = host;

    const availableGenerations = new Set<string>();
    const availablePresetTypes = new Set<string>();
    const generationNameMap = new Map<string, string>();
    const idResourcePresetMap = new Map<string, NormResourcePreset>();
    const presetZoneDiskTypes = new Map<NormResourcePreset, NormPresetZoneDisk[]>();

    resourcePresets.forEach((preset) => {
        idResourcePresetMap.set(preset.presetId, preset);
        availableGenerations.add(preset.generation);
        availablePresetTypes.add(preset.type);
        generationNameMap.set(preset.generation, preset.generationName);

        const diskTypes: NormPresetZoneDisk[] = [];
        presetZoneDiskTypes.set(preset, diskTypes);

        preset.zones.forEach((zone) => {
            diskTypes.push(...zone.diskTypes);
        });
    });

    resourcePresets
        .sort((a, b) => b.cpuFraction - a.cpuFraction)
        .sort((a, b) => a.memoryLimit - b.memoryLimit)
        .sort((a, b) => a.cpuLimit - b.cpuLimit)
        .sort((a, b) => Number(b.generation) - Number(a.generation));

    return {
        ...config,
        resourcePresets,
        presetZoneDiskTypes,
        defaultResources,
        idResourcePresetMap,
        availableGenerations,
        availablePresetTypes,
        generationNameMap,
    };
};
